import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import axios from './axiosConfig';
import Sidebar from './Sidebar';
import LanguageSwitcher from './LanguageSwitcher';
import UIIcon from './UIIcon';
import './Dashboard.css';

const Dashboard = () => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const loadUser = async () => {
      setUserLoading(true);
      try {
        const res = await axios.get('auth/whoami/');
        if (!cancelled) setCurrentUser(res.data || null);
      } catch (_) {
        if (!cancelled) setCurrentUser(null);
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    };
    loadUser();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post('auth/logout/');
    } catch (_) {
      // ignore logout transport errors; local session will still be abandoned by navigation
    } finally {
      try {
        sessionStorage.removeItem('whoami_ok_ts');
        sessionStorage.removeItem('justLoggedIn');
      } catch (_) {}
      navigate('/login', { replace: true });
    }
  };

  const pageMeta = useMemo(() => {
    const p = location.pathname;
    if (p.includes('/help-training')) {
      return { eyebrow: t('dashboard.meta.resources.eyebrow'), title: t('dashboard.meta.resources.title'), subtitle: t('dashboard.meta.resources.subtitle') };
    }
    if (p.includes('/reporting')) {
      return { eyebrow: t('dashboard.meta.reporting.eyebrow'), title: t('dashboard.meta.reporting.title'), subtitle: t('dashboard.meta.reporting.subtitle') };
    }
    if (p.includes('/reports/new-dsi')) {
      return { eyebrow: t('dashboard.meta.newDsi.eyebrow'), title: t('dashboard.meta.newDsi.title'), subtitle: t('dashboard.meta.newDsi.subtitle') };
    }
    if (p.includes('/patients/new')) {
      return { eyebrow: t('dashboard.meta.newPatient.eyebrow'), title: t('dashboard.meta.newPatient.title'), subtitle: t('dashboard.meta.newPatient.subtitle') };
    }
    if (p.includes('/patients/edit/')) {
      return { eyebrow: t('dashboard.meta.editPatient.eyebrow'), title: t('dashboard.meta.editPatient.title'), subtitle: t('dashboard.meta.editPatient.subtitle') };
    }
    if (p.includes('/patients/') && p.includes('/view')) {
      return { eyebrow: t('dashboard.meta.patientRecord.eyebrow'), title: t('dashboard.meta.patientRecord.title'), subtitle: t('dashboard.meta.patientRecord.subtitle') };
    }
    return { eyebrow: t('dashboard.meta.patients.eyebrow'), title: t('dashboard.meta.patients.title'), subtitle: t('dashboard.meta.patients.subtitle') };
  }, [location.pathname, t]);

  const roleLabel = currentUser?.role ? t(`role.${currentUser.role}`, { defaultValue: currentUser.role.replaceAll('_', ' ') }) : t('app.signedIn');

  const pathname = location.pathname.replace(/\/+$/, '');
  if (pathname === '/dashboard') {
    return <Navigate to="/dashboard/patients" replace />;
  }

  return (
    <div className="dashboard-layout">
      <Sidebar
        handleLogout={handleLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentUser={currentUser}
        userLoading={userLoading}
      />

      <header className="dashboard-header">
        <div className="header-left">
          <button
            className="menu-button"
            aria-label={t('nav.openSidebar', { defaultValue: 'Open navigation' })}
            onClick={() => setSidebarOpen(true)}
          >
            <UIIcon name="menu" size={21} />
          </button>
          <div className="page-heading">
            <span className="page-eyebrow">{pageMeta.eyebrow}</span>
            <div className="page-title-row">
              <h1 className="dashboard-title">{pageMeta.title}</h1>
              {currentUser?.permissions?.reporting && location.pathname.includes('/reporting') && (
                <span className="permission-chip"><UIIcon name="shield" size={13} /> {t('app.authorized')}</span>
              )}
            </div>
            <p>{pageMeta.subtitle}</p>
          </div>
        </div>

        <div className="header-right">
          <LanguageSwitcher />
          <div className="header-user" aria-label={t('dashboard.signedInUser')}>
            <div className="header-user-avatar">
              {((currentUser?.first_name?.[0] || currentUser?.username?.[0] || 'U') + (currentUser?.last_name?.[0] || '')).toUpperCase()}
            </div>
            <div className="header-user-copy">
              <strong>{[currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ') || currentUser?.username || t('app.user')}</strong>
              <span>{roleLabel}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <Outlet context={{ currentUser, userLoading }} />
      </main>
    </div>
  );
};

export default Dashboard;
